import { useRef } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import {
	Avatar, AvatarFallback, AvatarImage,
	Badge,
	Button,
	DefinitionList, DefinitionRow,
	DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
	IconButton,
	Spinner,
	TabActionButton,
	Tabs, TabsContent, TabsList, TabsTrigger,
} from '@rentflowdev/ui'
import { accountTypeLabel, signatoryBasisLabel } from '@/lib/account-labels'
import { formatPhone } from '@/lib/phone'
import { TaxRegimeDialog } from '@/components/tax-regime-dialog'
import { CurrencyDialog } from '@/components/currency-dialog'
import { CameraIcon, EllipsisVerticalIcon } from 'lucide-react'
import {
	useGetAccountQuery,
	useGetCompanyProfileQuery,
	useGenerateUploadUrlMutation,
	useGetMeQuery,
	useUpdateAccountTypeMutation,
	useUpdateUserMutation,
} from '@/api'
import { extractErrorMessage } from '@/lib/error-message'
import type { AccountType as ApiAccountType, Signatory, SignatoryBasis } from '@/types'
import { DEFAULT_CURRENCY } from '@/types'
import { useState } from 'react'
import { EditContractDataDialog } from '@/components/edit-contract-data-dialog'
import { EditContactsDialog } from '@/components/edit-contacts-dialog'
import { PencilIcon } from 'lucide-react'

type AccountType = ApiAccountType

export const Route = createFileRoute('/_app/settings/profile')({
	component: SettingsProfilePage,
})

const ACCOUNT_TYPES: { id: AccountType; titleKey: string; hintKey: string }[] = [
	{ id: 'INDIVIDUAL',   titleKey: 'settings.profile.accountType.individual',   hintKey: 'settings.profile.accountType.individualHint' },
	{ id: 'ENTREPRENEUR', titleKey: 'settings.profile.accountType.entrepreneur', hintKey: 'settings.profile.accountType.entrepreneurHint' },
	{ id: 'COMPANY',      titleKey: 'settings.profile.accountType.company',      hintKey: 'settings.profile.accountType.companyHint' },
]

function SettingsProfilePage() {
	const { t } = useTranslation()
	const { data: me } = useGetMeQuery()
	const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(me?.id ?? '', { skip: !me?.id })
	const { data: company, isLoading: isCompanyLoading } = useGetCompanyProfileQuery()
	const [updateAccountType, { isLoading: isUpdatingType }] = useUpdateAccountTypeMutation()

	const accountType: ApiAccountType = account?.accountType ?? 'INDIVIDUAL'

	const [editContractOpen, setEditContractOpen] = useState(false)
	const [editContactsOpen, setEditContactsOpen] = useState(false)
	const [taxRegimeOpen, setTaxRegimeOpen] = useState(false)
	const [currencyOpen, setCurrencyOpen] = useState(false)

	const taxRegime = account?.taxRegime ?? 'NONE'
	const currency = account?.currency ?? DEFAULT_CURRENCY

	if (isAccountLoading || isCompanyLoading) return <div className="p-4"><Spinner size="sm" /></div>

	return (
		<div className="flex h-full flex-col overflow-hidden">
			<ProfileHeader
				name={me?.name ?? account?.email ?? '—'}
				avatar={me?.avatar ?? undefined}
				email={account?.email ?? ''}
				accountType={accountType}
				onChangeType={(next) => void updateAccountType({ accountType: next })}
				isUpdatingType={isUpdatingType}
			/>

			<Tabs defaultValue="data" className="flex flex-1 flex-col overflow-hidden">
				<div className="border-b border-(--color-border) px-6">
					<TabsList variant="line">
						<TabsTrigger variant="line" value="data" className="group/tab">
							{t('settings.profile.subtabs.contract', { defaultValue: 'Данные для договора' })}
							<TabActionButton onClick={() => setEditContractOpen(true)}>
								<PencilIcon className="size-3.5" />
							</TabActionButton>
						</TabsTrigger>
						<TabsTrigger variant="line" value="contacts" className="group/tab">
							{t('settings.profile.subtabs.contacts', { defaultValue: 'Способы связи' })}
							<TabActionButton onClick={() => setEditContactsOpen(true)}>
								<PencilIcon className="size-3.5" />
							</TabActionButton>
						</TabsTrigger>
						<TabsTrigger variant="line" value="notifications">
							{t('settings.profile.subtabs.notifications', { defaultValue: 'Уведомления' })}
						</TabsTrigger>
					</TabsList>
				</div>

				<TabsContent value="data" className="flex-1 overflow-auto px-6 py-5">
					<div className="flex max-w-3xl flex-col gap-5">
						<DefinitionList title={t('settings.profile.contract.landlordInfo')}>
							<Row label={t('settings.profile.contract.fullName')} value={me?.name ?? company?.name} />
							<Row label={t('settings.profile.contract.inn')}      value={company?.inn} />
							<Row
								label={t('settings.profile.contract.registrationAddr')}
								value={accountType === 'INDIVIDUAL' ? company?.registrationAddr : company?.address}
							/>
							{accountType === 'INDIVIDUAL' && (
								<>
									<Row
										label={t('settings.profile.contract.passportSeriesNumber')}
										value={[company?.passportSeries, company?.passportNumber].filter(Boolean).join(' №') || undefined}
									/>
									<Row label={t('settings.profile.contract.passportIssuedBy')} value={company?.passportIssuedBy} />
								</>
							)}
							{accountType !== 'INDIVIDUAL' && (
								<>
									<Row
										label={accountType === 'ENTREPRENEUR' ? t('settings.profile.contract.ogrnIp') : t('settings.profile.contract.ogrn')}
										value={company?.ogrn}
									/>
									{accountType === 'COMPANY' && (
										<>
											<Row label={t('settings.profile.contract.kpp')}         value={company?.kpp} />
											<Row label={t('settings.profile.contract.companyName')} value={company?.name} />
										</>
									)}
								</>
							)}
						</DefinitionList>

						<DefinitionList title={t('settings.profile.contract.bankRequisites')}>
							<Row label={t('settings.profile.contract.bank')}            value={company?.bank} />
							<Row label={t('settings.profile.contract.checkingAccount')} value={company?.bankAccount} />
							<Row label={t('settings.profile.contract.corrAccount')}     value={company?.corrAccount} />
							<Row label={t('settings.profile.contract.bik')}             value={company?.bik} />
						</DefinitionList>

						<DefinitionList
							title={
								<span className="flex items-center justify-between gap-3">
									{t('settings.profile.taxation.title')}
									<Button
										variant="link"
										size="sm"
										className="h-auto p-0 text-sm font-normal"
										onClick={() => setTaxRegimeOpen(true)}
									>
										{t('settings.profile.taxation.change')}
									</Button>
								</span>
							}
						>
							<Row
								label={t('settings.profile.taxation.regime')}
								value={taxRegime === 'NONE'
									? undefined
									: t(`settings.profile.taxation.regimes.${taxRegime}`)}
							/>
							{taxRegime === 'USN' && (
								<Row
									label={t('settings.profile.taxation.incomeBand')}
									value={account?.vatVoluntary22
										? t('settings.profile.taxation.voluntary22')
										: account?.usnIncomeBand
											? t(`settings.profile.taxation.bands.${account.usnIncomeBand}`)
											: undefined}
								/>
							)}
							{taxRegime === 'OSNO' && account?.vatExemptArt145 && (
								<Row label={t('settings.profile.taxation.exemptArt145')} value={t('common.yes')} />
							)}
						</DefinitionList>

						<DefinitionList
								title={
									<span className="flex items-center justify-between gap-3">
										{t('settings.profile.currency.title')}
										<Button
											variant="link"
											size="sm"
											className="h-auto p-0 text-sm font-normal"
											onClick={() => setCurrencyOpen(true)}
										>
											{t('settings.profile.currency.change')}
										</Button>
									</span>
								}
							>
								<Row
									label={t('settings.profile.currency.default')}
									value={t(`currencies.${currency}`)}
								/>
							</DefinitionList>

						{resolveSignatories(company?.signatories, me?.name, company?.signatoryBasis).map((s, i, all) => (
							<DefinitionList
								key={s.id ?? `idx-${i}`}
								title={all.length > 1
									? t('settings.profile.contract.signatoryNumber', { n: i + 1 })
									: t('settings.profile.contract.signatorySection')
								}
							>
								<Row
									label={t('settings.profile.contract.fullName')}
									value={s.isSelf && me?.name
										? `${me.name} (${t('settings.profile.contract.selfMarker')})`
										: s.fullName}
								/>
								{!s.isSelf && (
									<Row label={t('settings.profile.contract.position')} value={s.position} />
								)}
								<Row label={t('settings.profile.contract.basis')} value={signatoryBasisLabel(t, s.basis)} />
								{s.basis === 'POWER_OF_ATTORNEY' && (
									<>
										<Row label={t('settings.profile.contract.proxyNumber')} value={s.proxyNumber} />
										<Row
											label={t('settings.profile.contract.proxyValidity')}
											value={s.proxyValidFrom && s.proxyValidUntil
												? `${formatDate(s.proxyValidFrom)} — ${formatDate(s.proxyValidUntil)}`
												: undefined}
										/>
									</>
								)}
							</DefinitionList>
						))}
					</div>
				</TabsContent>

				<TabsContent value="contacts" className="flex-1 overflow-auto px-6 py-5">
					<div className="max-w-2xl">
						<DefinitionList>
							<Row label={t('settings.profile.contact.phone')}    value={formatPhone(account?.phone)} />
							<Row label={t('settings.profile.contact.email')}    value={account?.email} />
							<Row label={t('settings.profile.contact.telegram')} value={account?.telegramId} />
						</DefinitionList>
					</div>
				</TabsContent>

				<TabsContent value="notifications" className="flex-1 overflow-auto px-6 py-5">
					<div className="flex max-w-2xl flex-col gap-3">
						{[
							{ key: 'newDeals',        label: t('settings.profile.notifications.newDeals') },
							{ key: 'scheduledShows',  label: t('settings.profile.notifications.scheduledShows') },
							{ key: 'paymentsOverdue', label: t('settings.profile.notifications.paymentsOverdue') },
						].map((row) => (
							<div key={row.key} className="flex flex-col gap-2 rounded-(--radius-md) border border-(--color-border) bg-(--color-card) p-4">
								<span className="text-sm font-[500]">{row.label}</span>
								<div className="flex gap-4">
									<label className="flex items-center gap-2 text-sm">
										<input type="checkbox" defaultChecked /> {t('settings.profile.notifications.channels.push')}
									</label>
									<label className="flex items-center gap-2 text-sm">
										<input type="checkbox" defaultChecked /> {t('settings.profile.notifications.channels.email')}
									</label>
									<label className="flex items-center gap-2 text-sm">
										<input type="checkbox" /> {t('settings.profile.notifications.channels.sms')}
									</label>
								</div>
							</div>
						))}
					</div>
				</TabsContent>
			</Tabs>

			{taxRegimeOpen && (
				<TaxRegimeDialog
					onOpenChange={setTaxRegimeOpen}
					taxRegime={taxRegime}
					usnIncomeBand={account?.usnIncomeBand}
					vatExemptArt145={account?.vatExemptArt145}
					vatVoluntary22={account?.vatVoluntary22}
				/>
			)}

			{currencyOpen && (
				<CurrencyDialog onOpenChange={setCurrencyOpen} currency={currency} />
			)}

			{account && (
				<EditContractDataDialog
					accountType={accountType}
					accountId={account.id}
					profile={company ?? undefined}
					me={me}
					open={editContractOpen}
					onOpenChange={setEditContractOpen}
				/>
			)}
			{account && (
				<EditContactsDialog
					accountId={account.id}
					phone={account.phone}
					email={account.email}
					telegramId={account.telegramId}
					open={editContactsOpen}
					onOpenChange={setEditContactsOpen}
				/>
			)}
		</div>
	)
}

function Row({ label, value }: { label: string; value?: string | null }) {
	const { t } = useTranslation()
	return <DefinitionRow label={label} value={value} placeholder={t('settings.profile.contract.notSpecified')} />
}

function resolveSignatories(
	rows: Signatory[] | undefined,
	myName: string | null | undefined,
	fallbackBasis: SignatoryBasis | undefined,
): Signatory[] {
	if (rows && rows.length > 0) return rows
	return [{
		fullName: myName ?? '',
		basis:    fallbackBasis ?? 'PERSONAL',
		isSelf:   true,
	}]
}

function formatDate(iso?: string): string | undefined {
	if (!iso) return undefined
	const d = new Date(iso)
	if (isNaN(d.getTime())) return iso
	return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function ProfileHeader({
	name, avatar, email, accountType, onChangeType, isUpdatingType,
}: {
	name:           string
	avatar?:        string
	email:          string
	accountType:    AccountType
	onChangeType:   (next: AccountType) => void
	isUpdatingType: boolean
}) {
	const { t } = useTranslation()
	const fileRef = useRef<HTMLInputElement>(null)
	const [generateUploadUrl] = useGenerateUploadUrlMutation()
	const [updateUser] = useUpdateUserMutation()
	const [uploading, setUploading] = useState(false)
	const [uploadError, setUploadError] = useState<string | undefined>()

	/**
	 * Загрузка идёт по тому же предподписанному адресу, что и фотографии
	 * объектов: маршрут лежит под `/properties`, но сам по себе он общий —
	 * принимает тип и имя файла и возвращает ссылку. Заводить второй такой же
	 * ради аватара значило бы удвоить и RPC, и хранилище.
	 */
	async function onPickFile(file: File) {
		setUploadError(undefined)
		setUploading(true)
		try {
			const { uploadUrl, publicUrl } = await generateUploadUrl({
				contentType: file.type,
				fileName:    file.name,
			}).unwrap()
			const res = await fetch(uploadUrl, {
				method:  'PUT',
				headers: { 'Content-Type': file.type },
				body:    file,
			})
			if (!res.ok) throw new Error(`HTTP ${res.status}`)
			await updateUser({ avatar: publicUrl }).unwrap()
		} catch (err) {
			setUploadError(extractErrorMessage(err))
		} finally {
			setUploading(false)
		}
	}
	const labelOf = (type: AccountType) => accountTypeLabel(t, type)
	const initials = name
		.trim()
		.split(/\s+/)
		.slice(0, 2)
		.map((p) => p[0]?.toUpperCase() ?? '')
		.join('') || 'CN'

	return (
		<header className="flex items-center gap-4 border-b border-(--color-border) px-6 py-5">
			<div className="flex flex-col gap-1">
				<button
					type="button"
					onClick={() => fileRef.current?.click()}
					disabled={uploading}
					aria-label={t('settings.profile.avatar.change')}
					title={t('settings.profile.avatar.change')}
					className="group relative rounded-(--radius-md)"
				>
					<Avatar className="size-12 rounded-(--radius-md)">
						{avatar && <AvatarImage src={avatar} alt={name} className="rounded-(--radius-md) object-cover" />}
						<AvatarFallback className="rounded-(--radius-md)">{initials}</AvatarFallback>
					</Avatar>
					<span className="absolute inset-0 flex items-center justify-center rounded-(--radius-md) bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
						{uploading
							? <Spinner size="sm" />
							: <CameraIcon className="size-4 text-white" />}
					</span>
				</button>
				<input
					ref={fileRef}
					type="file"
					accept="image/*"
					className="hidden"
					onChange={(e) => {
						const file = e.target.files?.[0]
						// Сбрасываем значение: иначе повторный выбор того же файла
						// не поднимет событие, и заменить аватар на тот же нельзя.
						e.target.value = ''
						if (file) void onPickFile(file)
					}}
				/>
			</div>
			<div className="flex flex-1 flex-col gap-1">
				<div className="flex items-center gap-2">
					<h1 className="text-xl font-[500]">{name}</h1>
					<Badge variant="info">{labelOf(accountType)}</Badge>
				</div>
				{email && <p className="text-sm text-(--color-muted-foreground)">{email}</p>}
				{uploadError && <p className="text-xs text-(--color-destructive)">{uploadError}</p>}
			</div>
			<DropdownMenu>
				<DropdownMenuTrigger render={<IconButton variant="ghost" size="sm" aria-label="Profile actions" />}>
					<EllipsisVerticalIcon className="size-4" />
				</DropdownMenuTrigger>
				<DropdownMenuContent align="end" className="min-w-52">
					{ACCOUNT_TYPES.map((type) => (
						<DropdownMenuItem
							key={type.id}
							disabled={isUpdatingType || accountType === type.id}
							onClick={() => onChangeType(type.id)}
						>
							{accountType === type.id ? '✓ ' : ''}{labelOf(type.id)}
						</DropdownMenuItem>
					))}
				</DropdownMenuContent>
			</DropdownMenu>
		</header>
	)
}

